import React, { CSSProperties, useEffect, useRef } from 'react';
import { SEAT_CONTAINER_HEIGHT, SEAT_CONTAINER_WIDTH } from 'constants/gameConstants';
import { useHandHistorySnapshotMode } from 'contexts/HandHistorySnapshotModeContext';
import gsap from 'gsap';
import { useTableSelector } from 'hooks/useTableSelector';

import { runFromSeatAction, runLabel } from 'components/UserActions/MultiRunActions/voteFromAction';

import FlameCanvas from './FlameCanvas';

export const VALID_ACTIONS = ['check', 'call', 'allin', 'fold', 'bet', 'raise', 'straddle'];

type PropType = {
  action: string;
  cardPostion: any;
};

export const actionColors: Record<string, { bg: string; border: string }> = {
  fold: { bg: 'rgba(200, 40, 40, 0.35)', border: 'rgba(200, 40, 40, 0.8)' },
  check: { bg: 'rgba(41, 128, 185, 0.25)', border: 'rgba(41, 128, 185, 0.7)' },
  call: { bg: 'rgba(39, 174, 96, 0.3)', border: 'rgba(39, 174, 96, 0.7)' },
  bet: { bg: 'rgba(233, 140, 38, 0.35)', border: 'rgba(233, 140, 38, 0.8)' },
  raise: { bg: 'rgba(233, 140, 38, 0.35)', border: 'rgba(233, 140, 38, 0.8)' },
  allin: { bg: 'rgba(200, 40, 40, 0.4)', border: 'rgba(255, 80, 40, 0.9)' },
  straddle: { bg: 'rgba(128, 90, 213, 0.3)', border: 'rgba(128, 90, 213, 0.7)' },
  // Mucked hand at showdown — a muted bronze so it reads distinct from the vivid
  // bet/raise orange while matching Unity's "MUCK" action label.
  muck: { bg: 'rgba(90, 78, 60, 0.5)', border: 'rgba(184, 150, 100, 0.85)' }
};

/**
 * A multi-run vote, ready to render as a speech bubble — or null if this action
 * is not one (EN-663 AC-2.4 / AC-3.4 / AC-5.2).
 *
 * The values in `p{seat}action` are PROTOCOL TOKENS, not copy ('No',
 * 'Run it Twice?', 'Run it Thrice?'), and renaming them breaks an already-loaded
 * client's reading of whether a seat has voted — see the note at the writer. So
 * the translation to player-facing copy happens here, at the render boundary,
 * and the wire is left alone.
 *
 * Colours are AC-5.2's universal tokens — 1X/decline blue, 2X green, 3X the
 * primary orange of Raise (Sevs, 2026-09-17, replacing the AC's original yellow)
 * — and are the bubble-side twins of `RUN_VARIANT` in VoteRunAction.tsx. All
 * three ARE `actionColors` entries now, referenced rather than retyped, so a
 * vote bubble cannot drift from the action row it borrows its palette from.
 */
const MULTI_RUN_COLORS: Record<number, { bg: string; border: string }> = {
  1: actionColors.check,
  2: actionColors.call,
  3: actionColors.raise
};

export const multiRunAction = (
  action: string
): { label: string; bg: string; border: string } | null => {
  const runs = runFromSeatAction(action);
  return runs ? { label: runLabel(runs), ...MULTI_RUN_COLORS[runs] } : null;
};

/**
 * Whether this seat's action represents an all-in straddle — a player whose
 * entire stack went into the straddle. Two signals reach the client:
 *   • the server may send action 'allin' (losing the straddle context), OR
 *   • useSeatLogic latches the straddle seat's action to 'straddle' for the
 *     pre-flop round, masking that 'allin'.
 * In both cases the ACTIVE straddler (hasStraddle = "1|<seatId>") sitting on a
 * 0 stack is all-in, so a 0-stack straddler is the reliable signal.
 *
 * When true, and only the straddle is in front (isOnlyTheStraddleInFront),
 * callers label the pill "STRADDLE (ALL IN)" and color it with the all-in
 * palette (not the straddle purple).
 *
 * Exported for unit testing.
 */
export const computeIsStraddleAllIn = (
  playerAction: string,
  hasStraddle: string | undefined,
  seatId: number,
  stack: unknown
): boolean => {
  const [straddleFlag, straddleSeat] = (hasStraddle ?? '0|0').split('|');
  const isActiveStraddler = straddleFlag !== '0' && straddleSeat === seatId.toString();
  if (!isActiveStraddler) return false;
  const action = (playerAction ?? '').toLowerCase();
  return action === 'allin' || (action === 'straddle' && Number(stack) === 0);
};

/**
 * Whether the chips in front of a straddler are no more than the straddle
 * itself (2x BB; a short stack posts less), i.e. nothing went in after it.
 * EN-11023 feedback: "STRADDLE (ALL IN)" is only for a stack that was exactly
 * the straddle — a straddler who goes all-in later in the round reads ALL IN.
 * Compared in whole cents, so an amount a float hair off its cent value
 * (0.30000000000000004) still counts as the straddle.
 *
 * Exported for unit testing.
 */
export const isOnlyTheStraddleInFront = (betDisplay: unknown, bb: unknown): boolean => {
  const inFront = Number(betDisplay);
  const bigBlind = Number(bb);
  if (!Number.isFinite(inFront) || !(bigBlind > 0)) return false;
  return Math.round(inFront * 100) <= Math.round(bigBlind * 2 * 100);
};

type ParticleConfig = {
  count: number;
  colors: string[];
  emoji?: string;
  speed: number;
  spread: number;
  fadeStyle: 'rise' | 'burst' | 'ripple' | 'sparkle' | 'flame';
};

const actionParticles: Record<string, ParticleConfig> = {
  bet: {
    count: 12,
    colors: ['#ff6b00', '#ff9500', '#ffcc00'],
    speed: 1.0,
    spread: 40,
    fadeStyle: 'flame'
  },
  raise: {
    count: 16,
    colors: ['#ff4400', '#ff6b00', '#ffcc00', '#ff0000'],
    speed: 1.0,
    spread: 45,
    fadeStyle: 'flame'
  },
  allin: {
    count: 22,
    colors: ['#ff0000', '#ff4400', '#ff6b00', '#ffcc00', '#ffffff'],
    speed: 1.2,
    spread: 50,
    fadeStyle: 'flame'
  },
  fold: {
    count: 8,
    colors: ['#888', '#aaa', '#666', '#999'],
    speed: 1.2,
    spread: 50,
    fadeStyle: 'rise'
  },
  check: {
    count: 1,
    colors: ['rgba(100,180,255,0.5)'],
    speed: 0.7,
    spread: 70,
    fadeStyle: 'ripple'
  },
  call: {
    count: 8,
    colors: ['#ffd700', '#ffec80', '#fff4b3'],
    speed: 0.7,
    spread: 45,
    fadeStyle: 'sparkle'
  },
  straddle: {
    count: 10,
    colors: ['#805ad5', '#9f7aea', '#b794f4'],
    speed: 1.0,
    spread: 40,
    fadeStyle: 'flame'
  }
};

const spawnParticles = (container: HTMLElement, config: ParticleConfig) => {
  const centerX = SEAT_CONTAINER_WIDTH / 2;
  const centerY = SEAT_CONTAINER_HEIGHT / 2;

  if (config.fadeStyle === 'ripple') {
    // Ripple: expanding ring
    const ring = document.createElement('div');
    ring.style.cssText = `
      position:absolute; top:${centerY}px; left:${centerX}px;
      width:0; height:0; border-radius:50%;
      border: 2px solid ${config.colors[0]};
      transform: translate(-50%,-50%);
      pointer-events: none;
    `;
    container.appendChild(ring);
    gsap.to(ring, {
      width: config.spread * 2.5,
      height: config.spread * 2.5,
      opacity: 0,
      borderWidth: 0.5,
      duration: config.speed,
      ease: 'power2.out',
      onComplete: () => ring.remove()
    });
    // Second ring, delayed
    const ring2 = ring.cloneNode() as HTMLElement;
    container.appendChild(ring2);
    gsap.to(ring2, {
      width: config.spread * 2,
      height: config.spread * 2,
      opacity: 0,
      borderWidth: 0.5,
      duration: config.speed * 0.8,
      delay: 0.15,
      ease: 'power2.out',
      onComplete: () => ring2.remove()
    });
    return;
  }

  for (let i = 0; i < config.count; i++) {
    const particle = document.createElement('div');
    const color = config.colors[Math.floor(Math.random() * config.colors.length)];

    if (config.fadeStyle === 'flame') {
      // Flame: particles spawn around the avatar edge and rise upward
      const radius = SEAT_CONTAINER_WIDTH / 2 + 2;
      const angle = (Math.PI * 2 * i) / config.count + (Math.random() - 0.5) * 0.4;
      const spawnX = centerX + Math.cos(angle) * radius;
      const spawnY = centerY + Math.sin(angle) * radius;
      const size = 5 + Math.random() * 8;

      particle.style.cssText = `
        position:absolute; top:${spawnY}px; left:${spawnX}px;
        width:${size}px; height:${size * 1.4}px;
        background: radial-gradient(ellipse at bottom, ${color} 0%, transparent 70%);
        border-radius: 50% 50% 20% 20%;
        pointer-events:none; z-index:20;
        opacity: 0.9;
        filter: blur(1px);
      `;
      container.appendChild(particle);

      // Rise upward slowly with flicker
      gsap.to(particle, {
        y: -(10 + Math.random() * 18),
        x: (Math.random() - 0.5) * 8,
        opacity: 0,
        scale: 0.2 + Math.random() * 0.3,
        duration: 0.8 + Math.random() * 0.6,
        delay: Math.random() * 0.4,
        ease: 'power1.out',
        onComplete: () => particle.remove()
      });
      continue;
    }

    const size = config.fadeStyle === 'sparkle' ? 4 + Math.random() * 4 : 6 + Math.random() * 6;

    particle.style.cssText = `
      position:absolute; top:${centerY}px; left:${centerX}px;
      width:${size}px; height:${size}px;
      background:${color}; border-radius:50%;
      pointer-events:none; z-index:20;
    `;
    container.appendChild(particle);

    const angle = (Math.PI * 2 * i) / config.count + (Math.random() - 0.5) * 0.5;
    const dist = config.spread * (0.5 + Math.random() * 0.5);

    if (config.fadeStyle === 'rise') {
      // Smoke rise: particles drift upward
      gsap.to(particle, {
        x: (Math.random() - 0.5) * config.spread,
        y: -(20 + Math.random() * 30),
        opacity: 0,
        scale: 1.5 + Math.random(),
        duration: config.speed * (0.8 + Math.random() * 0.4),
        delay: Math.random() * 0.15,
        ease: 'power1.out',
        onComplete: () => particle.remove()
      });
    } else if (config.fadeStyle === 'sparkle') {
      // Golden sparkle: quick flash
      gsap.fromTo(
        particle,
        { scale: 0, opacity: 1 },
        {
          x: Math.cos(angle) * dist * 0.7,
          y: Math.sin(angle) * dist * 0.7,
          scale: 1.5,
          opacity: 0,
          duration: config.speed * (0.5 + Math.random() * 0.3),
          delay: Math.random() * 0.2,
          ease: 'power2.out',
          onComplete: () => particle.remove()
        }
      );
    }
  }
};

const SeatAction = ({ action }: PropType) => {
  // A multi-run vote carries its own copy and palette (AC-5.2); everything else
  // renders from VALID_ACTIONS + actionColors as before.
  const multiRun = multiRunAction(action);
  const isShown = !!multiRun || VALID_ACTIONS.includes(action);
  const timerSpeed = useTableSelector((table) => table.game.speed);
  const elementRef = useRef<HTMLDivElement>(null);
  const particleRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);

  useEffect(() => {
    const el = elementRef.current;
    const particleContainer = particleRef.current;
    if (!el) return;

    if (timelineRef.current) {
      timelineRef.current.kill();
    }

    if (!isShown) {
      gsap.set(el, { opacity: 0, scale: 0.8 });
      return;
    }

    // Spawn particles
    const particleConfig = actionParticles[action.toLowerCase()];
    let particleInterval: ReturnType<typeof setInterval> | null = null;
    if (particleConfig && particleContainer) {
      spawnParticles(particleContainer, particleConfig);
      // Flames repeat to maintain the burning effect, others don't
      if (particleConfig.fadeStyle === 'flame') {
        particleInterval = setInterval(() => {
          if (particleContainer) {
            spawnParticles(particleContainer, particleConfig);
          }
        }, 600);
      }
    }

    const tl = gsap.timeline();
    // Pop in and stay visible — action persists until it changes
    tl.set(el, { opacity: 0, scale: 0.8 }).to(el, {
      opacity: 1,
      scale: 1,
      duration: 0.2,
      ease: 'back.out(1.5)'
    });

    timelineRef.current = tl;

    return () => {
      tl.kill();
      if (particleInterval) clearInterval(particleInterval);
      // Kill in-flight particle tweens so a seat that unmounts mid-burst doesn't
      // leave GSAP animating detached particle nodes for up to ~1.4s.
      if (particleContainer) gsap.killTweensOf(Array.from(particleContainer.children));
    };
  }, [action, isShown, timerSpeed]);

  if (!isShown) {
    return null;
  }

  const colors = multiRun ||
    actionColors[action.toLowerCase()] || {
      bg: 'rgba(80,80,80,0.3)',
      border: 'rgba(80,80,80,0.6)'
    };
  const actionLower = action.toLowerCase();
  const isFlameAction =
    actionLower === 'bet' ||
    actionLower === 'raise' ||
    actionLower === 'allin' ||
    actionLower === 'straddle';
  const flameIntensity = actionLower === 'allin' ? 1.5 : actionLower === 'raise' ? 1.2 : 1;
  const flameColor = actionLower === 'straddle' ? ('purple' as const) : ('fire' as const);

  return (
    <>
      {/* Canvas flame for bet/raise/allin */}
      {isFlameAction && <FlameCanvas intensity={flameIntensity} color={flameColor} />}
      {/* DOM particles for other actions */}
      {!isFlameAction && <div ref={particleRef} style={particleContainerStyle} />}
      <div
        ref={elementRef}
        style={{
          ...overlayStyle,
          backgroundColor: colors.bg,
          border: `2px solid ${colors.border}`,
          boxShadow: `0 0 12px 2px ${colors.border}, inset 0 0 15px ${colors.bg}`
        }}>
        <span style={textStyle}>{multiRun?.label ?? action.toUpperCase()}</span>
      </div>
    </>
  );
};

export default SeatAction;

/**
 * Lightweight version — just flames/particles, no overlay or text.
 * Used when the pill handles action display but we still want VFX.
 */
export const SeatActionEffects = ({ action }: { action: string }) => {
  const isHandHistorySnapshot = useHandHistorySnapshotMode();
  const timerSpeed = useTableSelector((table) => table.game.speed);
  const particleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHandHistorySnapshot) return;
    const particleContainer = particleRef.current;
    if (!VALID_ACTIONS.includes(action) || !particleContainer) return;

    const particleConfig = actionParticles[action.toLowerCase()];
    let particleInterval: ReturnType<typeof setInterval> | null = null;

    if (particleConfig && particleConfig.fadeStyle !== 'flame') {
      spawnParticles(particleContainer, particleConfig);
    }
    if (particleConfig?.fadeStyle === 'flame') {
      spawnParticles(particleContainer, particleConfig);
      particleInterval = setInterval(() => {
        spawnParticles(particleContainer, particleConfig);
      }, 600);
    }

    return () => {
      if (particleInterval) clearInterval(particleInterval);
      // Kill in-flight particle tweens so an unmount mid-burst doesn't leave
      // GSAP animating detached particle nodes for up to ~1.4s.
      gsap.killTweensOf(Array.from(particleContainer.children));
    };
  }, [action, isHandHistorySnapshot, timerSpeed]);

  if (isHandHistorySnapshot || !VALID_ACTIONS.includes(action)) return null;

  const actionLower = action.toLowerCase();
  const isFlameAction =
    actionLower === 'bet' ||
    actionLower === 'raise' ||
    actionLower === 'allin' ||
    actionLower === 'straddle';
  const flameIntensity = actionLower === 'allin' ? 1.5 : actionLower === 'raise' ? 1.2 : 1;
  const flameColor = actionLower === 'straddle' ? ('purple' as const) : ('fire' as const);

  return (
    <>
      {isFlameAction && <FlameCanvas intensity={flameIntensity} color={flameColor} />}
      {!isFlameAction && <div ref={particleRef} style={particleContainerStyle} />}
    </>
  );
};

const particleContainerStyle: CSSProperties = {
  position: 'absolute',
  width: SEAT_CONTAINER_WIDTH,
  height: SEAT_CONTAINER_HEIGHT,
  top: 0,
  left: 0,
  zIndex: 20,
  pointerEvents: 'none',
  overflow: 'visible'
};

const overlayStyle: CSSProperties = {
  position: 'absolute',
  width: SEAT_CONTAINER_WIDTH,
  height: SEAT_CONTAINER_HEIGHT,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10,
  top: 0,
  left: 0,
  pointerEvents: 'none'
};

const textStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  color: '#ffffff',
  letterSpacing: '0.08em',
  textShadow: '0 1px 2px rgba(0,0,0,0.4)'
};
